import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import { InputProps, Tooltip, ValidationMessage } from '@xsolla/ui-core';
import { ChevronUpDown, Star } from '@xsolla/ui-icons';

import { FlexParentBlock, SpaceBetweenBlock } from '../../../../styled';
import { SUPPLEMENTARY_FIELDS } from '../utils';
import { FieldOptions } from './FieldOptions';
import {
  DropdownIconWrapper,
  ExtraRightButton,
  FieldInputTitle,
  FieldInputType,
  InputWrapper,
  SelectFieldWrapper,
  SelectInputWrapper,
  SeparatingLine,
  WrappedInput
} from './style';
import { FieldTypes } from './types';

type Props = InputProps & {
  name: string;
  fieldType: string;
  label?: ReactNode;
  extraRight?: ReactNode;
  additional?: ReactNode;
  onChange: (option: { is_required: boolean; name: string; type: string }) => void;
  options?: {
    fieldType: FieldTypes;
  };
  required?: boolean;
};

const CustomAskField = ({
  name,
  options,
  required,
  fieldType,
  label,
  additional,
  errorMessage,
  extraRight,
  onChange,
  disabled,
  ...props
}: Props): JSX.Element => {
  const intl = useIntl();
  const theme = useTheme();

  const [visible, setVisible] = useState<boolean>(false);
  const [showHover, setShowHover] = useState<boolean>(false);
  const [isRequired, setIsRequired] = useState<boolean>(!!required);
  const [currField, setCurrField] = useState<string>(fieldType);
  const [fieldName, setFieldName] = useState<string>(name);

  useEffect(() => {
    onChange({
      is_required: isRequired,
      name: fieldName,
      type: currField
    });
  }, [fieldName, currField, isRequired]);

  const toggleExtraButton = () => {
    setIsRequired((prev) => !prev);
  };

  const toggleVisible = useCallback(() => {
    if (disabled) {
      return;
    }
    setVisible((prevVisible) => !prevVisible);
  }, []);

  const inputWrapper = useMemo(() => {
    return (
      <SelectFieldWrapper
        onClick={toggleVisible}
        onMouseEnter={() => setShowHover(true)}
        onMouseLeave={() => setShowHover(false)}>
        <FieldInputType>
          {intl.messages[`user-data-base.settings.supplementary-info.field-type.${currField}`]}
        </FieldInputType>

        <DropdownIconWrapper>
          <ChevronUpDown />
        </DropdownIconWrapper>

        <SeparatingLine />

        <WrappedInput
          disabled={disabled}
          hovered={showHover}
          name={name}
          onChange={(e) => {
            setFieldName(e.target.value);
          }}
          {...props}
        />

        <FieldOptions
          closeList={() => setVisible(false)}
          setFieldType={setCurrField}
          visible={visible}
        />
      </SelectFieldWrapper>
    );
  }, [currField]);

  return (
    <>
      <SpaceBetweenBlock>
        <FieldInputTitle>{label}</FieldInputTitle>
        {additional && <div>{additional}</div>}
      </SpaceBetweenBlock>
      <FlexParentBlock>
        <InputWrapper>
          <SelectInputWrapper disabled={!!disabled}>{inputWrapper}</SelectInputWrapper>
          <ValidationMessage>{errorMessage}</ValidationMessage>
        </InputWrapper>
        {!(
          fieldName === SUPPLEMENTARY_FIELDS.promoEmailAgreement ||
          fieldName === SUPPLEMENTARY_FIELDS.additionalAgreement
        ) && (
          <Tooltip
            content={intl.messages['user-data-base.settings.supplementary-info.field.required']}
            delayEnter={0}
            delayLeave={0}
            offset={6}
            placement="top-center">
            <ExtraRightButton palette="muted" onClick={toggleExtraButton}>
              <Star color={isRequired ? theme.colors.brand : theme.colors.muted} />
            </ExtraRightButton>
          </Tooltip>
        )}
      </FlexParentBlock>
    </>
  );
};

export default CustomAskField;
